import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ModalComponent, ModalData, ModalTitleIcons, OkCancelDialogActions } from '@modeso/aumico-lib-core-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';

// Local interface to avoid circular dependency
interface IAnnualReportDefaultSettings {
    currency: string;
    currentBalanceDate: string;
    previousBalanceDate: string;
    currentIncomeDate: string;
    previousIncomeDate: string;
}

@UntilDestroy()
@Component({
    selector: 'notes-actions-palette',
    templateUrl: './notes-actions-palette.component.html',
    styleUrls: ['./notes-actions-palette.component.scss'],
})
export class NotesActionsPaletteComponent implements OnChanges {
    @Input() isSaveDisabled: boolean;
    @Input() hasResetFromMappingButton: boolean | undefined;
    @Input() hasAddButton: boolean = false;
    @Input() hasEditButton: boolean = true;
    @Input() hasSaveButton: boolean = true;
    @Input() hasDeleteButton: boolean = true;
    @Input() hasCopyButton: boolean = false;
    @Input() hasFootnoteButton: boolean = false;
    @Input() hasTotalRowButton: boolean = false;
    @Input() footnote: boolean | undefined;
    @Input() transpose: boolean | undefined;
    @Input() flipSign: boolean | undefined;
    @Input() totalRow: boolean | undefined;
    @Input() compareYears: boolean | undefined;
    @Input() hideCurrentYear: boolean | undefined;
    @Input() showFiscalYear: boolean | undefined;
    @Input() showPreviousYear: boolean | undefined;
    @Input() pageBreak: boolean | undefined;
    @Input() hasFootnotes: boolean | undefined;
    @Input() settings: boolean | undefined;
    @Input() isHidden: boolean | undefined;
    @Input() showCurrency: boolean | undefined;
    /**
     * FX (REV-21512): display currency of a Functional-Currency report — undefined hides every
     * FX entry (non-FX reports, non-super-table components). When set, the hide-column flyout
     * gains checkboxes for the two display-currency twin columns and all four entries carry a
     * currency suffix («31.12.2025 · USD» / «31.12.2025 · CHF») to stay distinguishable.
     */
    @Input() fxDisplayCurrency: string | undefined;
    @Input() fxHideCurrentYear: boolean | undefined;
    @Input() fxHidePreviousYear: boolean | undefined;
    @Output() onEditItem = new EventEmitter<void>();
    @Output() onCopyItem = new EventEmitter<void>();
    @Output() onSaveItem = new EventEmitter<void>();
    @Output() onDeleteItem = new EventEmitter<void>();
    @Output() onAddItem = new EventEmitter<void>();
    @Output() onTransposeTriggered = new EventEmitter<void>();
    @Output() onFlipSignTriggered = new EventEmitter<void>();
    @Output() onTotalSignTriggered = new EventEmitter<void>();
    @Output() onCompareYearsTriggered = new EventEmitter<void>();
    @Output() onHideCurrentYearTriggered = new EventEmitter<void>();
    @Output() onShowPreviousYearTriggered = new EventEmitter<void>();
    @Output() onPageBreakTriggered = new EventEmitter<void>();
    @Output() onToggleSettings = new EventEmitter<boolean>();
    @Output() onToggleFootnote = new EventEmitter<boolean>();
    @Output() resetValuesFromMappingTriggered = new EventEmitter<boolean>();
    @Output() onShowFiscalYearTriggered = new EventEmitter<void>();
    @Output() onShowBalanceSheetDateTriggered = new EventEmitter<void>();
    @Output() onToggleVisibility = new EventEmitter<void>();
    @Output() onShowCurrencyTriggered = new EventEmitter<boolean>();
    @Output() onFxHideCurrentYearTriggered = new EventEmitter<void>();
    @Output() onFxHidePreviousYearTriggered = new EventEmitter<void>();

    @Input() isEditing: boolean;
    @Input() annualReportDefaultSettings: IAnnualReportDefaultSettings | undefined;

    // Flyout state
    isTableFlyoutOpen = false;
    flyoutPosition: { top: number; right: number; left?: number } = { top: 0, right: 0 };

    // Cached labels that update when inputs change
    currentYearLabel = 'Current Year';
    previousYearLabel = 'Previous Year';

    // Internal state for immediate UI updates
    internalShowFiscalYear: boolean | undefined = false;
    internalShowCurrency: boolean | undefined = undefined;

    constructor(
        private readonly dialog: MatDialog,
        private readonly i18next: I18NextService,

    ) {

    }

    ngOnChanges(changes: SimpleChanges): void {
        // Update internal state when showFiscalYear input changes
        if (changes['showFiscalYear']) {
            this.internalShowFiscalYear = this.showFiscalYear;
        }

        // Update internal state when showCurrency input changes
        if (changes['showCurrency']) {
            this.internalShowCurrency = this.showCurrency;
        }

        // Update labels when showFiscalYear or annualReportDefaultSettings change
        if (changes['showFiscalYear'] || changes['annualReportDefaultSettings']) {
            this.updateYearLabels();
        }
    }

    private updateYearLabels(): void {
        this.currentYearLabel = this.calculateCurrentYearLabel();
        this.previousYearLabel = this.calculatePreviousYearLabel();
    }

    private calculateCurrentYearLabel(): string {
        if (!this.annualReportDefaultSettings?.currentBalanceDate) {
            return this.i18next.t('aumico_notes_super_table_options_col_type_current_year').toString();
        }

        const currentDate = new Date(this.annualReportDefaultSettings.currentBalanceDate);
        const showFiscal = (this.internalShowFiscalYear ?? this.showFiscalYear) === true;

        if (showFiscal) {
            return currentDate.getFullYear().toString();
        } else {
            return currentDate.toLocaleDateString();
        }
    }

    private calculatePreviousYearLabel(): string {
        if (
            !this.annualReportDefaultSettings?.previousBalanceDate ||
            this.annualReportDefaultSettings.previousBalanceDate.trim() === '-'
        ) {
            return this.i18next.t('aumico_notes_super_table_options_col_type_previous_year').toString();
        }

        const previousDate = new Date(this.annualReportDefaultSettings.previousBalanceDate);
        const showFiscal = (this.internalShowFiscalYear ?? this.showFiscalYear) === true;

        if (showFiscal) {
            return previousDate.getFullYear().toString();
        } else {
            return previousDate.toLocaleDateString();
        }
    }

    onTranspose(): void {
        this.onTransposeTriggered.emit();
    }

    onFlipSign(): void {
        this.onFlipSignTriggered.emit();
    }

    onTotalSign(): void {
        this.onTotalSignTriggered.emit();
    }

    onCompareYears(event?: MouseEvent): void {
        if (event && this.compareYears) {
            event.preventDefault(); // Prevent default checkbox behavior only if compareYears was true
        }
        this.onCompareYearsTriggered.emit();
    }

    onHideCurrentYear(event?: MouseEvent): void {
        if (event && !this.hideCurrentYear) {
            event.preventDefault(); // Prevent default checkbox behavior only if hideCurrentYear was false
        }
        this.onHideCurrentYearTriggered.emit();
    }

    onShowPreviousYear(): void {
        this.onShowPreviousYearTriggered.emit();
    }

    // FX (REV-21512): twins carry no data, so no deletion-confirm dialog — direct toggles.
    onFxHideCurrentYear(): void {
        this.onFxHideCurrentYearTriggered.emit();
    }

    onFxHidePreviousYear(): void {
        this.onFxHidePreviousYearTriggered.emit();
    }

    onShowFiscalYear(): void {
        this.internalShowFiscalYear = true;
        this.onShowFiscalYearTriggered.emit();
        // Update labels immediately when fiscal year setting changes
        this.updateYearLabels();
    }

    onShowBalanceSheetDate(): void {
        this.internalShowFiscalYear = false;
        this.onShowBalanceSheetDateTriggered.emit();
        // Update labels immediately when balance sheet date setting changes
        this.updateYearLabels();
    }

    onShowCurrency(value: boolean): void {
        this.internalShowCurrency = value;
        this.onShowCurrencyTriggered.emit(value);
    }

    onPageBreak(): void {
        this.onPageBreakTriggered.emit();
    }

    onEdit(): void {
        this.onEditItem.emit();
        this.isEditing = true;
    }

    onCopy(): void {
        this.onCopyItem.emit();
    }

    onDelete(): void {
        this.onDeleteItem.emit();
    }

    onVisibilityToggle(): void {
        this.onToggleVisibility.emit();
    }

    onAdd(): void {
        this.onAddItem.emit();
    }

    toggleSettings(): void {
        this.settings = !this.settings;
        this.onToggleSettings.emit();
    }

    onFootnote(): void {
        this.onToggleFootnote.emit();
    }

    onSave(): void {
        if (!this.isSaveDisabled) {
            this.onSaveItem.emit();
            this.isEditing = false;
        }
    }

    openResetFormValuesFromMappingConfirmationDialog(): void {
        const dialogRef = this.dialog.open<ModalComponent, ModalData, OkCancelDialogActions>(ModalComponent, {
            disableClose: false,
            panelClass: 'custom-modalbox',
            data: {
                showCloseButton: true,
                titleIcon: ModalTitleIcons.WARNING,
                footerButtons: [
                    {
                        localizationKey:
                            'aumico_notes_super_table_reset_form_values_from_mapping_confirmation_dialog_ok',
                        action: OkCancelDialogActions.OK,
                        buttonColor: 'primary',
                    },
                    {
                        localizationKey:
                            'aumico_notes_super_table_reset_form_values_from_mapping_confirmation_dialog_cancel',
                        action: OkCancelDialogActions.CANCEL,
                        buttonColor: 'basic',
                    },
                ],
                content: {
                    text: ['aumico_notes_super_table_reset_form_values_from_mapping_confirmation_dialog_description'],
                    icon: 'document-information',
                },
            },
        });

        dialogRef
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                if (result === OkCancelDialogActions.OK) {
                    this.resetValuesFromMappingTriggered.emit(true);
                }
            });
    }


    get table(): boolean {
        if (this.compareYears != null || this.showFiscalYear != null || this.hideCurrentYear != null) {
            return true;
        } else {
            return false;
        }
    }

    onTableFlyout(event: MouseEvent): void {
        if (this.isTableFlyoutOpen) {
            this.closeTableFlyout();
            return;
        }

        // Calculate flyout position based on button click
        const buttonElement = event.target as HTMLElement;
        const rect = buttonElement.getBoundingClientRect();

        // Position flyout below and to the left of the button
        this.flyoutPosition = {
            top: rect.bottom + 5,
            right: window.innerWidth - rect.right,
            left: rect.left - 300,
        };

        this.isTableFlyoutOpen = true;
    }

    closeTableFlyout(): void {
        this.isTableFlyoutOpen = false;
    }

    getCurrentYearLabel(): string {
        return this.currentYearLabel;
    }

    getPreviousYearLabel(): string {
        return this.previousYearLabel;
    }
}
